import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MessageSquare, Phone, Mail } from 'lucide-react';
import { useFeedback } from '../../context';
import { useProposalData } from '../../context';
import logo from '../../assets/logo.svg';
import logoGray from '../../assets/logo-gray.png';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { toggleSidebar, getFeedbackCount } = useFeedback();
  const proposal = useProposalData();
  const isServiceProposal = proposal.proposalType === 'service';

  const navItems = isServiceProposal
    ? [
        { id: 'equipment-spotlight', label: 'Equipment' },
        { id: 'tasks', label: 'Service Tasks' },
        { id: 'estimate-summary', label: 'Summary' },
        { id: 'next-steps', label: 'Next Steps' },
      ]
    : [
        { id: 'equipment', label: 'Equipment' },
        { id: 'addons', label: 'Add-ons' },
        { id: 'services', label: 'Services' },
        { id: 'pricing', label: 'Pricing' },
        { id: 'next-steps', label: 'Next Steps' },
      ];
  const feedbackCount = getFeedbackCount();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        {/* Top Bar */}
        <div className={`transition-all duration-300 ${isScrolled ? 'h-0 overflow-hidden' : 'h-auto'}`}>
          <div className="bg-hul-navy text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-2 text-sm">
                <div className="hidden sm:flex items-center gap-6">
                  <a
                    href={`tel:${proposal.salesperson.phone}`}
                    className="flex items-center gap-2 hover:text-hul-red transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>{proposal.salesperson.phone}</span>
                  </a>
                  <a
                    href={`mailto:${proposal.salesperson.email}`}
                    className="flex items-center gap-2 hover:text-hul-red transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span>{proposal.salesperson.email}</span>
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white/70">Your Rep:</span>
                  <span className="font-medium">{proposal.salesperson.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <motion.div
              className="flex items-center"
              whileHover={{ scale: 1.02 }}
            >
              <a href="#" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <img
                  src={isScrolled ? logo : logoGray}
                  alt="Herc-U-Lift"
                  className="h-10 lg:h-12 w-auto"
                />
              </a>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-4 py-2 font-heading text-sm uppercase tracking-wider transition-all duration-200 hover:text-hul-red ${
                    isScrolled ? 'text-hul-navy' : 'text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* Feedback Button */}
              {proposal.feedbackEnabled && (
                <motion.button
                  onClick={toggleSidebar}
                  data-pdf-hide
                  className={`relative flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    isScrolled
                      ? 'bg-hul-navy text-white hover:bg-hul-navy-dark'
                      : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="hidden sm:inline font-medium text-sm">Feedback</span>
                  {feedbackCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-hul-orange text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {feedbackCount}
                    </span>
                  )}
                </motion.button>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${
                  isScrolled ? 'text-hul-navy hover:bg-gray-100' : 'text-white hover:bg-white/10'
                }`}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Industrial Accent Line */}
        <div className={`h-1 bg-gradient-to-r from-hul-red via-hul-red-light to-hul-red transition-opacity duration-300 ${isScrolled ? 'opacity-100' : 'opacity-0'}`} />
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.nav
              className="absolute top-20 left-4 right-4 bg-white rounded-xl shadow-2xl overflow-hidden"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="p-2">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="w-full text-left px-4 py-3 font-heading text-hul-navy uppercase tracking-wider hover:bg-gray-50 rounded-lg transition-colors"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {item.label}
                  </motion.button>
                ))}
              </div>
              <div className="border-t border-gray-100 p-4">
                <div className="text-sm text-gray-500 mb-2">Contact Your Rep</div>
                <a
                  href={`tel:${proposal.salesperson.phone}`}
                  className="flex items-center gap-3 text-hul-navy font-medium"
                >
                  <Phone className="w-4 h-4" />
                  {proposal.salesperson.phone}
                </a>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
